import { useEffect, useState, type CSSProperties } from 'react';
import type { SelfState } from '../../../shared/protocol';
import { getSettings } from '../game/settings';
import { formatNumber, t, type Key } from '../i18n';
import { Icon } from './icons';

const fmtMin = (s: number) => `${Math.floor(s / 60)} min`;

/** Tranches de la roue (degrés depuis le haut, sens horaire), proportionnelles aux probabilités : 50 / 35 / 14,999 / 0,001 %.
 *  La tranche Singularité est élargie à 1,8° pour rester visible ; doit correspondre au dégradé de .wheel (hud.css). */
const SLICES: Record<string, [number, number]> = { coins: [0, 180], tokens: [180, 306], pass: [306, 358.2], singularity: [358.2, 360] };

/** Rotation finale : 4 tours, puis le milieu de la tranche tirée sous le pointeur (en haut). */
export function wheelStop(result: string): number {
  const [a, b] = SLICES[result] ?? SLICES.coins;
  return 1440 + (360 - (a + b) / 2);
}

/** Probabilités et règles, affichées avant l'encaissement comme après (dossier §8 : « probabilités consultables avant choix »). */
function Odds() {
  return (
    <div className="roulette-odds">
      <p className="wip">{t('rOdds')}</p>
      <p className="wip">{t('rOddsTokens')}</p>
      <p className="wip">{t('rRules')}</p>
    </div>
  );
}

/** Progression vers l'éligibilité et bouton d'encaissement (volontaire, hors combat, hors zone violette). */
export function RouletteWidget({ r, onCashout }: { r: NonNullable<SelfState['roulette']>; onCashout: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const row = (ok: boolean, text: string) => (
    <li className={ok ? 'ok' : ''}>
      <span className="r-check" aria-hidden>
        {ok ? <Icon name="check" size={12} /> : null}
      </span>
      {text}
    </li>
  );
  const canTry = r.eligible && r.account && r.verified && r.week < 3 && !r.pending;
  return (
    <div className="roulette-widget" data-testid="roulette">
      <div className="lb-title">{t('roulette')}</div>
      <ul>
        {row(r.level >= r.need.level, t('rLevel', { n: r.need.level }))}
        {row(r.activeSeconds >= r.need.activeSeconds, t('rActive', { a: fmtMin(r.activeSeconds), b: fmtMin(r.need.activeSeconds) }))}
        {row(r.chainRaw >= r.need.raw, t('rRaw', { a: formatNumber(r.chainRaw), b: formatNumber(r.need.raw) }))}
        {row(r.distinctKills >= r.need.distinctKills || r.monsters >= r.need.monsters, t('rFeats', { k: r.distinctKills, m: r.monsters }))}
      </ul>
      <div className="wip">{t('rWeek', { n: r.week })}</div>
      <details className="roulette-details">
        <summary>{t('rOddsTitle')}</summary>
        <Odds />
      </details>
      {!r.account ? (
        <div className="wip">{t('rNeedAccount')}</div>
      ) : !r.verified ? (
        <div className="wip">{t('rNeedVerify')}</div>
      ) : confirm ? (
        <div className="roulette-confirm">
          <p>{t('rConfirm')}</p>
          <Odds />
          <div className="hud-row">
            <button
              className="btn btn-primary btn-small"
              onClick={() => {
                setConfirm(false);
                onCashout();
              }}
            >
              {t('rCashout')}
            </button>
            <button className="btn btn-ghost btn-small" onClick={() => setConfirm(false)}>
              {t('cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button className="btn btn-primary btn-small" disabled={!canTry} onClick={() => setConfirm(true)}>
          {r.pending ? t('loading') : t('rCashout')}
        </button>
      )}
    </div>
  );
}

/** Révélation d'un résultat déjà enregistré par le serveur (l'animation ne décide rien). */
export function RouletteReveal({ result, detail, onClose }: { result: string; detail: Record<string, unknown>; onClose: () => void }) {
  const reduced = getSettings().reducedMotion;
  const [shown, setShown] = useState(reduced);
  useEffect(() => {
    if (reduced) return;
    const id = setTimeout(() => setShown(true), 3500);
    return () => clearTimeout(id);
  }, [reduced]);
  let text = '';
  if (result === 'coins') text = t('rResCoins', { n: Number(detail.coins ?? 5000) });
  else if (result === 'tokens')
    text = Object.entries((detail.tokens as Record<string, number>) ?? {})
      .map(([k, v]) => `${t(`token_${k}` as Key)} ×${v}`)
      .join(', ');
  else if (result === 'pass') text = t('rResPass', { n: Number(detail.passLevels ?? 0), c: Number(detail.convertedCoins ?? 0) });
  else text = detail.item ? t('rResSingularity', { v: String(detail.item) }) : t('rResCoins', { n: 5000 });
  return (
    <div className="death" data-testid="roulette-reveal">
      <div className="death-card roulette-card">
        <h2 className="display">{t('roulette')}</h2>
        {!shown ? (
          <div className="wheel-wrap" role="img" aria-label={t('loading')}>
            <span className="wheel-pointer" aria-hidden />
            {/* La roue s'arrête sur la tranche du résultat déjà enregistré par le serveur (elle ne décide rien). */}
            <div className="wheel" style={{ '--stop': `${wheelStop(result)}deg` } as CSSProperties}>
              <div className="wheel-inner" />
            </div>
          </div>
        ) : (
          <>
            <p className="roulette-result display">{text}</p>
            <button className="btn btn-primary" onClick={onClose} autoFocus>
              {t('close')}
            </button>
          </>
        )}
        <Odds />
      </div>
    </div>
  );
}
